import { Hidden } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useMemo } from 'react';

import { useGetFooterContent } from '../../api';
import {
  CopyrightWithLinks,
  ContentAllPages,
  ContentFrontPage,
  FollowUs,
  LanguageSelector,
  MarketExceptions,
  MobileApps,
  PaymentMethods,
  SiteLinks,
  Subscribe,
} from '../../components';
import type { FooterContext } from '../../contexts';
import { FooterProvider, defaultFooterContext } from '../../contexts';
import { useReopenCookiePreferences } from '../../hooks/useReopenCookiePreferences';
import { FooterAppSkeleton } from '../FooterAppSkeleton';
import { FOOTER_CONTENT } from './constants';
import { useIsMobileOffice } from './hooks';
import { FourthColumn, HighlightGrid, Highlight, BottomContent, Root } from './styles';

export const FooterContent: FC = () => {
  const { getTestId } = useTestId();
  const isMobileOffice = useIsMobileOffice();

  useReopenCookiePreferences();

  const { data: footerContent } = useGetFooterContent();

  const footerProviderValue = useMemo((): FooterContext => {
    if (!footerContent) {
      return {
        ...defaultFooterContext,
        isMobileOffice,
      };
    }

    return {
      isMobileOffice,
      ...footerContent,
    };
  }, [footerContent, isMobileOffice]);

  if (!footerContent) {
    return <FooterAppSkeleton />;
  }

  return (
    <FooterProvider value={footerProviderValue}>
      <Root data-testid={getTestId(FOOTER_CONTENT)}>
        <Subscribe />
        <Highlight>
          <Hidden mdUp />
          <HighlightGrid isMobileOffice={isMobileOffice}>
            <SiteLinks />
            <FourthColumn>
              <MobileApps />
            </FourthColumn>
          </HighlightGrid>
        </Highlight>
        <BottomContent isMobileOffice={isMobileOffice}>
          <FollowUs />
          <Hidden mdUp>
            <MobileApps />
          </Hidden>
          <ContentFrontPage />
          <ContentAllPages />
          <MarketExceptions />
          <CopyrightWithLinks />
          <LanguageSelector />
          <PaymentMethods />
        </BottomContent>
      </Root>
    </FooterProvider>
  );
};
